import React, { useState, useCallback, useContext, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  FormGroup
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import {
  FaCloudUploadAlt,
  FaFileExcel,
  FaDownload,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaImages,
  FaGlobe,
  FaTachometerAlt
} from 'react-icons/fa';
import { MdEuro } from 'react-icons/md';
import { Description, Clear, CheckCircle, CloudUpload } from '@mui/icons-material';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { formatLogTime, formatDateTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const ExportImages = () => {
  const theme = useTheme();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState([]);
  const [region, setRegion] = useState('US');
  const [highVelocityItems, setHighVelocityItems] = useState(false);

  // Refs for async processing
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const currentFileIndexRef = useRef(0);

  // Helper function to add logs to activity context
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const onDrop = useCallback((acceptedFiles) => {
    const supportedFiles = acceptedFiles.filter(file =>
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'text/csv' ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls') ||
      file.name.toLowerCase().endsWith('.csv')
    );

    if (supportedFiles.length !== acceptedFiles.length) {
      showNotification('Only Excel (.xlsx, .xls) and CSV files are supported', 'warning');
    }

    if (supportedFiles.length === 0) return;

    const newFiles = supportedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'ready',
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: true
  });

  const removeFile = (id) => {
    setFiles(files => files.filter(f => f.id !== id));
  };

  // Fetch status from server and update activity log
  const fetchStatus = async (currentProcessId, activityProcessId, uiProcessId, fileData) => {
    try {
      const response = await apiCall(API_ENDPOINTS.EXPORT_IMAGES_STATUS(currentProcessId), {
        method: 'GET',
      });

      // Update activity log with server logs
      if (response.logs && response.logs.length > 0) {
        setLogs(activityProcessId, response.logs);
      }

      // Update progress
      if (response.progressPercentage !== undefined) {
        updateActivityProcess(activityProcessId, { progressPercentage: response.progressPercentage });
        updateProcess(uiProcessId, {
          progress: response.progressPercentage,
          status: response.status === 'completed' ? 'completed' : 'Processing images...'
        });
      }

      // Check if completed or errored
      if (response.status === 'completed') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        // Auto-download if not already triggered
        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          // Prefer S3-based download (persists across server restarts)
          if (response.outputKey) {
            await downloadResultFile(null, activityProcessId, response.fileName, response.outputKey);
          } else {
            await downloadResultFile(currentProcessId, activityProcessId, response.fileName);
          }
        }

        // Update file status to completed
        setFiles(prev => prev.map(f =>
          f.id === fileData.id ? {
            ...f,
            status: 'completed',
            progress: 100,
            result: { processedCount: response.processedItems, message: response.message },
            serverProcessId: currentProcessId
          } : f
        ));

        setResults(prev => [...prev, {
          id: fileData.id,
          fileName: fileData.name,
          processedCount: response.processedItems,
          message: response.message,
          serverProcessId: currentProcessId,
          timestamp: new Date()
        }]);

        completeActivityProcess(activityProcessId, true);
        showNotification('Image export completed!', 'success');
        setProcessing(false);

        setTimeout(() => {
          removeProcess(uiProcessId);
        }, 3000);
      } else if (response.status === 'error') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        setFiles(prev => prev.map(f =>
          f.id === fileData.id ? {
            ...f,
            status: 'error',
            error: response.message || 'Processing failed'
          } : f
        ));

        completeActivityProcess(activityProcessId, false);
        showNotification(`Processing failed: ${response.message}`, 'error');
        setProcessing(false);

        setTimeout(() => {
          removeProcess(uiProcessId);
        }, 5000);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download result file from server (prefers S3 key if available)
  const downloadResultFile = async (serverProcessId, activityProcessId, fileName, outputKey) => {
    try {
      const downloadUrl = outputKey
        ? API_ENDPOINTS.EXPORT_IMAGES_DOWNLOAD(encodeURIComponent(outputKey))
        : API_ENDPOINTS.EXPORT_IMAGES_DOWNLOAD_RESULT(serverProcessId);

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'ExportedImages.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(activityProcessId, `✓ Excel downloaded: ${fileName}`);
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(activityProcessId, `✗ Error downloading file: ${err.message}`);
    }
  };

  const processFiles = async () => {
    if (files.length === 0) {
      showNotification('Please select files to process', 'warning');
      return;
    }

    setProcessing(true);
    downloadTriggeredRef.current = false;

    // For now, process only the first file (can be extended for multiple files)
    const fileData = files[0];

    // Generate a unique process ID for activity log
    const activityProcessId = `export-images-${Date.now()}`;

    // Add to global activity log
    addActivityProcess(activityProcessId, `Export Item Images (${region})`, 'Export Item Images');
    addLog(activityProcessId, `Starting export images processing...`);
    addLog(activityProcessId, `Region: ${region}`);
    addLog(activityProcessId, `High Velocity Items: ${highVelocityItems}`);
    addLog(activityProcessId, `File: ${fileData.name}`);

    const uiProcessId = addProcess({
      name: 'Export Images Processing',
      status: 'processing',
      progress: 0
    });

    try {
      setFiles(prev => prev.map(f =>
        f.id === fileData.id ? { ...f, status: 'processing', progress: 0 } : f
      ));

      const formData = new FormData();
      formData.append('file', fileData.file);
      formData.append('region', region);
      formData.append('highVelocityItems', highVelocityItems);

      addLog(activityProcessId, `⏳ Uploading file to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 10 });
      updateProcess(uiProcessId, {
        progress: 10,
        status: 'Uploading file...'
      });

      // Make API call to start processing
      const response = await apiCall(API_ENDPOINTS.EXPORT_IMAGES_PROCESS, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addLog(activityProcessId, `⏳ Processing started on server...`);

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus(serverProcessId, activityProcessId, uiProcessId, fileData);
        }, 1000);
        setIntervalRef(activityProcessId, intervalRef.current);
      } else {
        throw new Error('No process ID returned from server');
      }

    } catch (error) {
      console.error('Processing error:', error);
      updateProcess(uiProcessId, {
        status: 'error',
        progress: 0
      });

      addLog(activityProcessId, `✗ Error: ${error.message}`);
      completeActivityProcess(activityProcessId, false);

      setFiles(prev => prev.map(f =>
        f.id === fileData.id ? {
          ...f,
          status: 'error',
          error: error.message
        } : f
      ));

      showNotification(`Processing failed: ${error.message}`, 'error');
      setProcessing(false);

      setTimeout(() => {
        removeProcess(uiProcessId);
      }, 5000);
    }
  };

  const downloadFile = async (serverProcessId, fileName) => {
    try {
      const response = await fetch(API_ENDPOINTS.EXPORT_IMAGES_DOWNLOAD_RESULT(serverProcessId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Download failed', 'error');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.EXPORT_IMAGES_TEMPLATE);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Export_Images_Template_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('Template downloaded successfully', 'success');
    } catch (error) {
      console.error('Template download error:', error);
      showNotification('Template download failed', 'error');
    }
  };

  const clearAll = () => {
    setFiles([]);
    setResults([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'primary';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <FaCheckCircle />;
      case 'processing': return <FaClock />;
      case 'error': return <FaExclamationCircle />;
      default: return <FaFileExcel />;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Export Item Images" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          EXPORT ITEM IMAGES
        </Typography>

      <Grid container spacing={3}>
        {/* Configuration & Upload Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            {/* Region Selection */}
            <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
              <CardContent>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Select Region
                  </FormLabel>
                  <RadioGroup
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    row
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="US"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FaGlobe />
                          <span>US</span>
                          <Chip label="Default" size="small" sx={{ height: 20 }} />
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="EU"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MdEuro size={16} />
                          <span>EU</span>
                        </Box>
                      }
                    />
                  </RadioGroup>
                  <FormGroup sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={highVelocityItems}
                          onChange={(e) => setHighVelocityItems(e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FaTachometerAlt size={16} />
                          <Typography>High Velocity Items Only</Typography>
                        </Box>
                      }
                    />
                  </FormGroup>
                </FormControl>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card sx={{ mb: 3, bgcolor: files.length > 0 ? getCardBackground(theme) : 'transparent' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <FaFileExcel color={TOOL_COLORS.EXCEL.primary} size={24} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Import File (Excel/CSV)
                  </Typography>
                  {files.length > 0 && (
                    <Tooltip title={`${files.length} file(s) selected`}>
                      <FaCheckCircle color="#228B22" />
                    </Tooltip>
                  )}
                </Box>

                {/* Drop Zone */}
                <Box
                  {...getRootProps()}
                  sx={{
                    border: `2px dashed ${isDragActive ? TOOL_COLORS.EXCEL.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive
                      ? TOOL_COLORS.EXCEL.light
                      : files.length > 0
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                        : 'transparent',
                    transition: 'all 0.3s ease',
                    mb: 2,
                    '&:hover': {
                      borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.EXCEL.primary,
                      bgcolor: files.length > 0
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                        : TOOL_COLORS.EXCEL.light,
                    }
                  }}
                >
                  <input {...getInputProps()} />
                  {files.length > 0 ? (
                    <>
                      <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {files.length} file(s) selected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click or drop more files to add
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.EXCEL.primary : theme.palette.text.secondary, mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {isDragActive ? 'Drop files here' : 'Drop Excel/CSV files or click to browse'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Supports .xlsx, .xls, and .csv files with ItemID column
                      </Typography>
                    </>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={downloadTemplate}
                    startIcon={<FaDownload style={{ fontSize: '14px' }} />}
                    sx={{
                      borderColor: alpha(theme.palette.text.primary, 0.2),
                      color: alpha(theme.palette.text.primary, 0.7),
                      bgcolor: 'transparent',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2.5,
                      py: 0.8,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: alpha(theme.palette.text.primary, 0.4),
                        color: theme.palette.text.primary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05),
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    Download Template
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* File List */}
            {files.length > 0 && (
              <Box sx={{ mb: 3, maxHeight: 300, overflow: 'auto' }}>
                {files.map((fileData) => (
                  <Box
                    key={fileData.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      mb: 1,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <Description sx={{ color: TOOL_COLORS.EXCEL.primary, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fileData.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        ({formatFileSize(fileData.size)})
                      </Typography>
                      {fileData.status === 'processing' && <Chip label="processing" size="small" color="primary" sx={{ ml: 1 }} />}
                      {fileData.status === 'completed' && <Chip label="completed" size="small" color="success" sx={{ ml: 1 }} />}
                      {fileData.status === 'error' && <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {fileData.serverProcessId && (
                        <Tooltip title="Download result">
                          <IconButton
                            size="small"
                            onClick={() => downloadFile(fileData.serverProcessId, `ExportedImages-${fileData.name}`)}
                            sx={{ color: theme.palette.success.main }}
                          >
                            <FaDownload />
                          </IconButton>
                        </Tooltip>
                      )}
                      {fileData.status === 'ready' && (
                        <IconButton size="small" onClick={() => removeFile(fileData.id)} sx={{ color: theme.palette.error.main }}>
                          <Clear fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                onClick={processFiles}
                disabled={files.length === 0 || processing}
                startIcon={<FaImages />}
                sx={{
                  bgcolor: TOOL_COLORS.EXCEL.primary,
                  '&:hover': { bgcolor: TOOL_COLORS.EXCEL.hover },
                  px: 4
                }}
              >
                {processing ? 'Processing...' : 'Export Images'}
              </Button>
              <Button
                variant="outlined"
                onClick={clearAll}
                disabled={processing}
                sx={{ borderColor: theme.palette.divider }}
              >
                Clear All
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Status & Instructions */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Status & Instructions
            </Typography>

            {/* Progress */}
            {processing && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Processing image export...
                </Typography>
                <LinearProgress color="primary" />
              </Box>
            )}

            {/* Instructions */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              How it works:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 3 }}>
              1. Select region (US/EU)<br />
              2. Enable high velocity items filter if needed<br />
              3. Upload Excel/CSV file with ItemID column<br />
              4. System matches ItemIDs with available images<br />
              5. Download Excel file with embedded images<br /><br />
              <strong>Features:</strong><br />
              • Automatic image matching from configured directories<br />
              • Image compression and optimization<br />
              • US and EU region support<br />
              • High velocity items filtering
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Expected Format */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Expected File Format:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
              Excel/CSV file should contain:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip label="ItemID" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default ExportImages;